// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import type { TyEditorDiscount } from "@/types";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type DiscountPopupDetails = {
    productName: string;
    discount: TyEditorDiscount;
    onConfirm: (discount: TyEditorDiscount) => void;
};

type Props = {
    onClose: () => void;
    details?: DiscountPopupDetails;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * A discount is three fields that only make sense together, and it is off far more often than on —
 * three grid columns would be dead weight on every row. It lives behind one cell instead.
 */
export default function DiscountPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const [active, setActive] = useState(details?.discount?.active ?? false);
    const [type, setType] = useState<"percentage" | "fixed">(details?.discount?.type ?? "percentage");
    const [amount, setAmount] = useState(String(details?.discount?.amount ?? ""));

    if (!details) return null;

    const handleConfirm = () => {
        const parsed = Number(amount);
        // An "off" discount is stored as no discount at all, which is what the menu template expects.
        const next: TyEditorDiscount = active && Number.isFinite(parsed) && parsed > 0 ? { active: true, type, amount: parsed } : null;
        details.onConfirm(next);
        onClose();
    };

    return (
        <div className="discount-popup">
            <div className="discount-popup-head">
                <p className="discount-popup-title">{t.menuEditor.discount}</p>
                <p className="discount-popup-subtitle">{details.productName || "—"}</p>
            </div>

            <button
                type="button"
                className={`discount-popup-toggle${active ? " discount-popup-toggle--on" : ""}`}
                onClick={() => setActive((prev) => !prev)}
                aria-pressed={active}
            >
                <span className="discount-popup-toggle-dot" />
                {active ? t.menuEditor.discountOn : t.menuEditor.discountOff}
            </button>

            {active && (
                <>
                    <div className="discount-popup-types">
                        <button
                            type="button"
                            className={`discount-popup-type${type === "percentage" ? " discount-popup-type--on" : ""}`}
                            onClick={() => setType("percentage")}
                        >
                            {t.menuEditor.discountPercentage}
                        </button>
                        <button
                            type="button"
                            className={`discount-popup-type${type === "fixed" ? " discount-popup-type--on" : ""}`}
                            onClick={() => setType("fixed")}
                        >
                            {t.menuEditor.discountFixed}
                        </button>
                    </div>

                    <div className="discount-popup-field">
                        <span className="discount-popup-label">{t.menuEditor.discountAmount}</span>
                        <input
                            className="discount-popup-input"
                            type="number"
                            inputMode="decimal"
                            value={amount}
                            onChange={(event) => setAmount(event.target.value)}
                            autoFocus
                        />
                    </div>
                </>
            )}

            <div className="discount-popup-actions">
                <button type="button" className="discount-popup-cancel" onClick={onClose}>
                    {t.menuEditor.cancel}
                </button>
                <button type="button" className="discount-popup-confirm" onClick={handleConfirm}>
                    {t.menuEditor.done}
                </button>
            </div>
        </div>
    );
}
